/**
 * TractionPanel — "what got traction and from where" for the last 7/30
 * days, backed by the daily_views rollup collection.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { TrendingUp, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => {
  try { return window.localStorage.getItem("storylo_admin_token") || "storylo-admin"; }
  catch { return "storylo-admin"; }
};

const countryFlag = (cc) =>
  /^[A-Z]{2}$/.test(cc) && cc !== "XX"
    ? String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
    : "🌐";

const countryName = (cc) => {
  if (cc === "XX") return "Unknown";
  try { return new Intl.DisplayNames(["en"], { type: "region" }).of(cc) || cc; }
  catch { return cc; }
};

export default function TractionPanel() {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/traction?days=${d}`, {
        headers: { "X-Admin-Token": token() },
      });
      setData(res);
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); load(days); }}
        className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-white/10 text-[color:var(--muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-1.5"
        data-testid="traction-open"
        title="Top stories and traffic sources over the last 7 or 30 days"
      >
        <TrendingUp className="w-3.5 h-3.5" /> Traction
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
          data-testid="traction-overlay"
        >
          <div className="panel w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="traction-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-lg flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[color:var(--accent)]" /> Traction
              </div>
              <button onClick={() => setOpen(false)} className="text-[color:var(--muted)] hover:text-white" data-testid="traction-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex gap-2 mb-4">
              {[7, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => { setDays(d); load(d); }}
                  className={`text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border ${days === d ? "border-[color:var(--accent)] text-[color:var(--accent)]" : "border-white/15 text-[color:var(--muted)]"}`}
                  data-testid={`traction-days-${d}`}
                >
                  Last {d} days
                </button>
              ))}
            </div>

            {loading ? (
              <div className="text-xs text-[color:var(--muted)] flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
            ) : !data ? (
              <div className="text-xs text-[color:var(--muted)]">Could not load traction data.</div>
            ) : (
              <>
                <div className="text-xs text-[color:var(--muted)] mb-3" data-testid="traction-total">
                  {data.total_views} story views in the last {data.days} days
                  {data.total_views === 0 && " — tracking started when this feature deployed, so numbers build from today"}
                </div>
                {data.top_stories.length > 0 && (
                  <div className="mb-5">
                    <div className="label-eyebrow mb-2">Top stories</div>
                    {data.top_stories.map((s, i) => (
                      <div key={s.video_id} className="flex items-center gap-2 py-1 text-sm">
                        <span className="text-[color:var(--muted)] w-5 text-right">{i + 1}.</span>
                        <a href={`/s/${s.slug}`} target="_blank" rel="noreferrer" className="flex-1 truncate hover:text-[color:var(--accent)]">{s.title}</a>
                        <span className="text-[color:var(--accent)] font-semibold">{s.views}</span>
                      </div>
                    ))}
                  </div>
                )}
                {data.sources.length > 0 && (
                  <div className="mb-5">
                    <div className="label-eyebrow mb-2">Where visitors came from</div>
                    <div className="flex flex-wrap gap-2">
                      {data.sources.map((s) => (
                        <span key={s.source} className="text-xs px-2.5 py-1 rounded-full border border-white/15">
                          {s.source} · <span className="text-[color:var(--accent)]">{s.views}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {(data.countries || []).length > 0 && (
                  <div data-testid="traction-countries">
                    <div className="label-eyebrow mb-2">Viewer countries</div>
                    <div className="flex flex-wrap gap-2">
                      {data.countries.map((c) => (
                        <span key={c.country} className="text-xs px-2.5 py-1 rounded-full border border-white/15">
                          {countryFlag(c.country)} {countryName(c.country)} · <span className="text-[color:var(--accent)]">{c.views}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {(data.countries || []).length === 0 && data.total_views > 0 && (
                  <div className="text-[11px] text-[color:var(--muted)] mt-2">
                    Country tracking starts from this deploy — locations appear as new views come in.
                  </div>
                )}
                {data.podcast && (
                  <div className="mt-5" data-testid="traction-podcast">
                    <div className="label-eyebrow mb-2">Podcast plays</div>
                    {data.podcast.total_plays === 0 ? (
                      <div className="text-[11px] text-[color:var(--muted)]">
                        No plays counted yet — Spotify &amp; Apple plays register here as listeners stream episodes.
                      </div>
                    ) : (
                      <>
                        <div className="text-xs text-[color:var(--muted)] mb-2">
                          {data.podcast.total_plays} episode plays in the last {data.days} days
                        </div>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {data.podcast.apps.map((a) => (
                            <span key={a.app} className="text-xs px-2.5 py-1 rounded-full border border-white/15">
                              {a.app} · <span className="text-[color:var(--accent)]">{a.plays}</span>
                            </span>
                          ))}
                        </div>
                        {data.podcast.top_episodes.map((e, i) => (
                          <div key={e.video_id} className="flex items-center gap-2 py-1 text-sm">
                            <span className="text-[color:var(--muted)] w-5 text-right">{i + 1}.</span>
                            <span className="flex-1 truncate">{e.title}</span>
                            <span className="text-[color:var(--accent)] font-semibold">{e.plays}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
